/**
 * What the company owes its two insurance policies, month by month, on the
 * crews it actually paid.
 *
 * `insurance.ts` budgets insurance on an estimate before the work is done.
 * This is the other end: once a crew has been paid, what that payment costs
 * in workers comp and general liability. Lysa reports comp to the fund every
 * month, and liability is audited at the end of the policy year against what
 * was really paid out, so both want the same list — every paid work order,
 * by the month the crew was paid, split by whether the crew carries its own
 * cover.
 *
 * The rates and the certificate rules are `orderInsurance`'s, unchanged. The
 * certificate is read **as it stands today**, not as it stood on the day the
 * crew was paid (Jonathon, 2026-10-07), so a past month can move when a
 * certificate is uploaded or runs out. See ADR 0275.
 */

import { documentTotal } from "./ladder.js";
import { addCents, toCents, type Cents } from "./money.js";
import {
  orderInsurance,
  type InsuranceStanding,
  type InsuredCrew,
  type OrderInsurance,
  type TradeList,
} from "./insurance.js";
import type { CostDocument } from "./types.js";

/**
 * The liability policy year, and what the carrier priced it on.
 *
 * Policy BAS-83535-1, Crum & Forster, 09/02/2026 to 09/02/2027. The carrier
 * rated the year on $300,000 of subcontract cost (ADR 0107); the audit bills
 * the difference if the company pays its crews more than that. Renewal
 * replaces all three, here and in the Edge Function's copy; once `to` has
 * passed, the screen and the email say the year on file has ended until it
 * is done (issue #848, docs/DEPLOYMENT.md, "Renewing the liability policy").
 */
export const GL_POLICY_YEAR = {
  from: "2026-09-02",
  to: "2027-09-02",
  /** Subcontract cost the premium was written on, in cents. */
  pricedOn: 30_000_000 as Cents,
} as const;

/** A work order as this report reads it. */
export interface PaidWorkOrder extends CostDocument {
  id: string;
  order_number?: number | null;
  /** The "Crew paid" tick — Mark paid on the payout board, or on the order. */
  paid_at: string | null;
  /** Set only when the payout was approved for a different amount. */
  payout_amount?: number | string | null;
  trades: TradeList;
  crew_id?: string | null;
  crew?: (InsuredCrew & { id?: string | null }) | null;
  job?: {
    job_number?: number | null;
    is_test?: boolean | null;
    contact?: { full_name?: string | null } | null;
  } | null;
}

/** One paid work order and what it costs to insure. */
export interface OwedOrder {
  order: PaidWorkOrder;
  /** `YYYY-MM-DD` in Birmingham. */
  paidOn: string;
  /** What the crew was paid. */
  paid: Cents;
  insurance: OrderInsurance;
}

/** One crew's share of a month. */
export interface OwedCrew {
  /** The crew id, or "" for work orders nobody chose a crew for. */
  key: string;
  crew: PaidWorkOrder["crew"];
  glStanding: InsuranceStanding;
  wcStanding: InsuranceStanding;
  paid: Cents;
  generalLiability: Cents;
  workersComp: Cents;
  orders: OwedOrder[];
}

/** Paid to crews, split by whether they carry the policy themselves. */
export interface CoverSplit {
  /** Paid to crews with a current certificate for this policy. */
  ownCover: Cents;
  /** Paid to everybody else: no certificate, a lapsed one, or no crew. */
  ours: Cents;
  /** What the company owes the policy for the month. */
  owed: Cents;
}

export interface OwedMonth {
  /** `YYYY-MM`. */
  month: string;
  /** Everything paid to crews that month. */
  paid: Cents;
  workersComp: CoverSplit;
  generalLiability: CoverSplit;
  /** Dearest first. */
  crews: OwedCrew[];
}

/** A timestamp as the calendar date it was in Birmingham. */
export function paidDay(timestamp: string | null | undefined): string | null {
  const raw = (timestamp ?? "").trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // PostgREST can hand back `2026-08-27 14:31:01+00`: a space for the T and a
  // two-digit offset, neither of which `Date` reads everywhere.
  const iso = raw
    .replace(" ", "T")
    .replace(/([+-])(\d{2})$/, "$1$2:00")
    .replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
}

/**
 * What the crew was paid on this order: the approved amount when somebody
 * changed it, the order's own total otherwise — `payoutTotal`'s rule.
 */
function amountPaid(order: PaidWorkOrder): Cents {
  return order.payout_amount == null || order.payout_amount === ""
    ? documentTotal(order)
    : toCents(order.payout_amount);
}

/**
 * Which paid work orders count.
 *
 * Our own employees are on payroll, which is reported separately, and are
 * left out: Jonathon, 2026-10-12, "don't mention employees comp. we don't
 * operate like that" (ADR 0275). Estimates still charge them at the full
 * rate (ADR 0107). Practice jobs paid nobody.
 */
function counts(order: PaidWorkOrder): boolean {
  if (!order.paid_at) return false;
  if (order.crew?.is_in_house === true) return false;
  if (order.job?.is_test === true) return false;
  return true;
}

/** Every paid work order, priced, oldest first. */
export function owedOrders(
  orders: readonly PaidWorkOrder[],
  today: string,
): OwedOrder[] {
  const out: OwedOrder[] = [];
  for (const order of orders) {
    if (!counts(order)) continue;
    const paidOn = paidDay(order.paid_at);
    if (!paidOn) continue;
    const paid = amountPaid(order);
    out.push({
      order,
      paidOn,
      paid,
      insurance: orderInsurance({
        paid,
        trades: order.trades,
        crew: order.crew,
        today,
      }),
    });
  }
  return out.sort((a, b) => a.paidOn.localeCompare(b.paidOn));
}

function split(
  rows: readonly OwedOrder[],
  standing: (r: OwedOrder) => InsuranceStanding,
  owed: (r: OwedOrder) => Cents,
): CoverSplit {
  const own = rows.filter((r) => standing(r) === "insured");
  const ours = rows.filter((r) => standing(r) !== "insured");
  return {
    ownCover: addCents(...own.map((r) => r.paid)),
    ours: addCents(...ours.map((r) => r.paid)),
    owed: addCents(...rows.map(owed)),
  };
}

/**
 * Paid work orders grouped by the month the crew was paid, newest month
 * first, with each month's comp and liability and who they were for.
 */
export function insuranceOwedByMonth(
  orders: readonly PaidWorkOrder[],
  today: string,
): OwedMonth[] {
  const byMonth = new Map<string, OwedOrder[]>();
  for (const row of owedOrders(orders, today)) {
    const month = row.paidOn.slice(0, 7);
    byMonth.set(month, [...(byMonth.get(month) ?? []), row]);
  }

  return [...byMonth.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([month, rows]) => {
      const byCrew = new Map<string, OwedOrder[]>();
      for (const row of rows) {
        const key = row.order.crew?.id ?? row.order.crew_id ?? "";
        byCrew.set(key, [...(byCrew.get(key) ?? []), row]);
      }
      const crews: OwedCrew[] = [...byCrew.entries()].map(([key, list]) => {
        const first = list[0] as OwedOrder;
        return {
          key,
          crew: first.order.crew ?? null,
          glStanding: first.insurance.glStanding,
          wcStanding: first.insurance.wcStanding,
          paid: addCents(...list.map((r) => r.paid)),
          generalLiability: addCents(
            ...list.map((r) => r.insurance.generalLiability),
          ),
          workersComp: addCents(...list.map((r) => r.insurance.workersComp)),
          orders: list,
        };
      });
      crews.sort(
        (a, b) =>
          b.generalLiability +
          b.workersComp -
          (a.generalLiability + a.workersComp),
      );

      return {
        month,
        paid: addCents(...rows.map((r) => r.paid)),
        workersComp: split(
          rows,
          (r) => r.insurance.wcStanding,
          (r) => r.insurance.workersComp,
        ),
        generalLiability: split(
          rows,
          (r) => r.insurance.glStanding,
          (r) => r.insurance.generalLiability,
        ),
        crews,
      };
    });
}

/** The liability policy year so far, against what it was priced on. */
export interface LiabilityYear {
  from: string;
  to: string;
  /** Paid to crews since the policy started. */
  paid: Cents;
  /** Of that, paid to crews with their own liability certificate. */
  paidOwnCover: Cents;
  /** Liability those payments cost, at the policy's rates. */
  owed: Cents;
  /** Subcontract cost the premium was written on. */
  pricedOn: Cents;
  /** How far through the year today is, 0 to 1. */
  elapsed: number;
  /** Today is on or after `to`: the year on file is over, and not renewed. */
  ended: boolean;
  /**
   * What the year comes to if the rest goes as it has so far. Null on day
   * one, and null once the year has ended: a closed year is not projected.
   */
  paidOnPace: Cents | null;
  owedOnPace: Cents | null;
}

const DAY_MS = 86_400_000;
const dayNumber = (day: string): number =>
  Date.parse(`${day}T00:00:00Z`) / DAY_MS;

/**
 * Where the liability year stands: paid so far, what that costs, and where it
 * is heading against the $300,000 the policy was priced on. Payouts outside
 * the policy year are left out — they belong to another policy.
 */
export function liabilityYear(
  orders: readonly PaidWorkOrder[],
  today: string,
): LiabilityYear {
  const { from, to, pricedOn } = GL_POLICY_YEAR;
  const rows = owedOrders(orders, today).filter(
    (r) => r.paidOn >= from && r.paidOn < to && r.paidOn <= today,
  );
  const length = dayNumber(to) - dayNumber(from);
  const gone = Math.min(
    Math.max(dayNumber(today) - dayNumber(from) + 1, 0),
    length,
  );
  const elapsed = length > 0 ? gone / length : 0;
  const ended = today >= to;

  const paid = addCents(...rows.map((r) => r.paid));
  const owed = addCents(...rows.map((r) => r.insurance.generalLiability));
  const pace = (amount: Cents): Cents | null =>
    elapsed > 0 && !ended ? (Math.round(amount / elapsed) as Cents) : null;

  return {
    from,
    to,
    paid,
    paidOwnCover: addCents(
      ...rows
        .filter((r) => r.insurance.glStanding === "insured")
        .map((r) => r.paid),
    ),
    owed,
    pricedOn,
    elapsed,
    ended,
    paidOnPace: pace(paid),
    owedOnPace: pace(owed),
  };
}
